import { brand, neutral, semantic } from "@/constants/colors";
import { fonts } from "@/constants/fonts";
import type React from "react";
import type { ReactNode } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  type TextStyle,
  useColorScheme,
  View,
  type ViewStyle,
} from "react-native";
import ThemedText from "../core/ThemedText";

type ButtonVariant = "filled" | "outline" | "ghost";
type ButtonSize = "sm" | "md" | "lg";
type ButtonColor = "primary" | "success" | "warning" | "error" | "neutral";
type IconPosition = "left" | "right";

interface ButtonProps {
  onPress?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  color?: ButtonColor;
  disabled?: boolean;
  loading?: boolean;
  children?: ReactNode;
  style?: ViewStyle | ViewStyle[];
  textStyle?: TextStyle;
  icon?: ReactNode;
  iconPosition?: IconPosition;
  isIconOnly?: boolean;
  iconStyle?: ViewStyle;
}

export const Button: React.FC<ButtonProps> = ({
  onPress,
  variant = "filled",
  size = "md",
  color = "primary",
  disabled = false,
  loading = false,
  children,
  style,
  textStyle,
  icon,
  iconPosition = "left",
  isIconOnly = false,
  iconStyle,
}) => {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";

  const sizeStyles: Record<
    ButtonSize,
    { height: number; fontSize: number; padding: number }
  > = {
    sm: { height: 36, fontSize: 14, padding: 12 },
    md: { height: 44, fontSize: 16, padding: 16 },
    lg: { height: 55, fontSize: 18, padding: 20 },
  };

  const getButtonColor = () => {
    switch (color) {
      case "primary":
        return isDark ? brand.primary.dark : brand.primary.main;
      case "success":
        return isDark ? semantic.success.dark : semantic.success.main;
      case "warning":
        return semantic.warning.main;
      case "error":
        return isDark ? semantic.error.dark : semantic.error.main;
      case "neutral":
        return isDark ? neutral[600] : neutral[300];
    }
  };

  const getVariantStyle = () => {
    const baseStyle: ViewStyle = {
      borderRadius: 12,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
    };

    const buttonColor = getButtonColor();

    switch (variant) {
      case "filled":
        return {
          ...baseStyle,
          backgroundColor: buttonColor,
        };
      case "outline":
        return {
          ...baseStyle,
          backgroundColor: "transparent",
          borderColor: buttonColor,
          borderWidth: 1,
        };
      case "ghost":
        return {
          ...baseStyle,
          backgroundColor: "transparent",
        };
    }
  };

  const getTextColor = () => {
    if (disabled) return isDark ? neutral[500] : neutral[400];

    const buttonColor = getButtonColor();

    switch (variant) {
      case "filled":
        if (color === "primary") {
          return "#FFFFFF";
        }
        return isLightColor(buttonColor) ? neutral[900] : "#FFFFFF";
      case "outline":
      case "ghost":
        // For outline and ghost variants, use black in light mode and white in dark mode
        return isDark ? "#FFFFFF" : "#000000";
    }
  };

  const isLightColor = (hexColor: string) => {
    const r = Number.parseInt(hexColor.slice(1, 3), 16);
    const g = Number.parseInt(hexColor.slice(3, 5), 16);
    const b = Number.parseInt(hexColor.slice(5, 7), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  };

  const renderContent = () => {
    if (loading) {
      return <ActivityIndicator color={getTextColor()} />;
    }

    if (isIconOnly && icon) {
      return <View style={iconStyle}>{icon}</View>;
    }

    const textContent = children && (
      <ThemedText
        style={StyleSheet.flatten([
          {
            fontSize: sizeStyles[size].fontSize,
            color: getTextColor(),
            textAlign: "center",
            marginBottom: 0,
            fontFamily: fonts.semiBold,
          },
          textStyle,
        ])}
      >
        {children}
      </ThemedText>
    );

    if (icon) {
      return (
        <View style={styles.contentContainer}>
          {iconPosition === "left" && (
            <View style={[styles.iconContainer, iconStyle]}>{icon}</View>
          )}
          {textContent}
          {iconPosition === "right" && (
            <View style={[styles.iconContainer, iconStyle]}>{icon}</View>
          )}
        </View>
      );
    }

    return textContent;
  };

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled || loading}
      style={[
        getVariantStyle(),
        {
          height: sizeStyles[size].height,
          paddingHorizontal: isIconOnly
            ? sizeStyles[size].height / 3
            : sizeStyles[size].padding,
          opacity: disabled ? 0.5 : 1,
          width: isIconOnly ? sizeStyles[size].height : undefined,
        },
        style,
      ]}
    >
      {renderContent()}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  iconContainer: {
    marginHorizontal: 8,
  },
});

export default Button;
